import { ReactNode, useCallback, useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { ExploreWidgetConfig, VisitorFilter } from "../config"
import { useExplorer, type ExplorerState } from "../providers"
import { todayIso } from "./filters/flexDates"
import { boundsFiltering } from "./map/initialView"
import {
  DEFAULT_WINDOW_AFTER_WEEKS,
  DEFAULT_WINDOW_BEFORE_WEEKS,
  WINDOW_AFTER_WEEKS_RANGE,
  WINDOW_BEFORE_WEEKS_RANGE,
  applyVerdicts,
  clampWeeks,
  rescueRangeLabel,
  rescueWindows,
  validRange,
  windowKey,
  type RescueChip,
  type RescueWindow,
} from "./filters/pro/zeroResultsRescue"
import { useWindowAvailability } from "./filters/pro/useWindowAvailability"

/**
 * Module strings (Renderer.php:110-118; flexible-search.js:508-525, :663-667,
 * :769). English literals: the widget has no translation layer and the
 * surrounding empty state hardcodes its copy the same way.
 */
export const RESCUE_I18N = {
  noMatchesFor: 'No matches for',
  tryThese: 'Try one of these instead:',
  datesWithHomes: 'These nearby dates have homes available:',
  tryDifferentDates: 'Try different dates.',
  availableAria: '%s, available',
} as const

/** The configured suggestion window, clamped to the module bounds. */
export function rescueWindowWeeks(options: ExploreWidgetConfig): { before: number; after: number } {
  return {
    before: clampWeeks(options.searchbar.flexDates.windowBeforeWeeks, DEFAULT_WINDOW_BEFORE_WEEKS, WINDOW_BEFORE_WEEKS_RANGE),
    after: clampWeeks(options.searchbar.flexDates.windowAfterWeeks, DEFAULT_WINDOW_AFTER_WEEKS, WINDOW_AFTER_WEEKS_RANGE),
  }
}

/** The slice of explorer state the gate reads. */
export type RescueGateState = Pick<
  ExplorerState,
  'visitorFilters' | 'flexDates' | 'mapSearchActive' | 'mapVisible' | 'mapBounds' | 'mobileSheetActive'
>

/**
 * Whether the rescue owns the empty state for this search: the flexible-dates
 * module is on, the search is an EXACT dated one, the range is real, and no
 * search-as-move area is narrowing the results.
 *
 * A FLEXIBLE search keeps the generic empty state on purpose — the module
 * renders its flex ranges as unverified chips there (js:432-436) and shows no
 * adjusted notice for a flex pivot ("a refinement, not an adjustment",
 * js:680-681); on the platform the FlexAltDates strip under the bar already
 * carries those ranges.
 *
 * A search narrowed by an APPLIED map area keeps the generic message too
 * (Codex, two independent cold passes): the probes ask about the search as a
 * whole, like the module's check, so under a viewport a confirmed window is
 * either an empty page (bounds kept) or a silently widened search (bounds
 * dropped), and every settled drag would re-fire the batch. No claim is the
 * honest claim there; zoom out or switch search-as-move off and the rescue
 * returns.
 */
export function rescueApplies(options: ExploreWidgetConfig, state: RescueGateState): boolean {
  if (!options.searchbar.flexDates.enabled) return false
  if (state.flexDates) return false
  if (boundsFiltering(state)) return false
  return validRange(state.visitorFilters.checkin, state.visitorFilters.checkout)
}

/** A window chip; verified ones carry the module's CSS check and an accessible name saying so (js:767-770). */
export function RescueChipButton({ chip, onPick }: { chip: RescueChip; onPick: (w: RescueWindow) => void }) {
  return (
    <button
      type="button"
      className={`hfse-flex-rescue-chip${chip.verified ? ' hfse-flex-rescue-chip-available' : ''}`}
      aria-label={chip.verified ? RESCUE_I18N.availableAria.replace('%s', chip.label) : undefined}
      data-checkin={chip.checkin}
      data-checkout={chip.checkout}
      onClick={() => onPick(chip)}
    >
      {chip.label}
    </button>
  )
}

type Props = {
  /** The grid's own query filters (bounds and browse price column included) — the probes must ask exactly what the grid asks. */
  filters: Partial<VisitorFilter>
  /** The generic empty state, rendered whenever the rescue does not apply. */
  fallback: ReactNode
}

/**
 * Zero-results rescue (#1265) — toolkit `flexible-search` half (b), the exact
 * dated branch of the module's empty-state box (`renderExactEmpty`,
 * flexible-search.js:493-536, verified by `verifyEmptyChips`, :639-670).
 *
 * "No matches for <searched window>." followed by same-length windows shifted
 * across nearby weeks. Every window is probed against Central and each verdict
 * folds in as it lands: windows with nothing bookable disappear, confirmed ones
 * get the check and the subtitle strengthens to "These nearby dates have homes
 * available:". Windows Central could not answer stay, unverified. If nothing
 * survives, "Try different dates."
 *
 * Picking a chip applies its window as a new EXACT search in place and records
 * the searched dates so the results page can say plainly that the dates were
 * adjusted (AdjustedDatesNotice).
 */
export default function ZeroResultsRescue({ filters, fallback }: Props) {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, rescuePivot } = useExplorer()
  const platformUuid = options.filter?.platform

  const applies = rescueApplies(options, state)
  const checkin = state.visitorFilters.checkin ?? ''
  const checkout = state.visitorFilters.checkout ?? ''
  const { before, after } = rescueWindowWeeks(options)
  const today = todayIso()

  const windows = useMemo(
    () => (applies ? rescueWindows(checkin, checkout, today, before, after) : []),
    [applies, checkin, checkout, today, before, after],
  )
  const { verdictFor, settled } = useWindowAvailability(feedId, filters, windows, platformUuid, applies && windows.length > 0)

  const pivot = useCallback((w: RescueWindow) => {
    // One transition (providers.tsx applyRescuePivot): the window becomes the
    // exact search, the searched dates become the adjustment's reference
    // (js:529-531), any flexible selection goes. The map is untouched — the
    // gate above never offers the rescue while an applied area narrows the search.
    rescuePivot({ checkin: w.checkin, checkout: w.checkout, from: { checkin, checkout } })
  }, [rescuePivot, checkin, checkout])

  if (!applies) return <>{fallback}</>

  // Never blocks the render: every window shows unverified at once and each
  // verdict folds in as it lands. Progressive rather than at-settle (the
  // module folds its single batch at once): one hung probe must not keep
  // Central's answered "nothing bookable" windows clickable.
  const folded = applyVerdicts(windows, verdictFor)
  const subtitle = folded.chips.length === 0
    ? RESCUE_I18N.tryDifferentDates
    : folded.verified > 0
      ? RESCUE_I18N.datesWithHomes
      : RESCUE_I18N.tryThese

  return (
    <div className="hfse-flex-rescue" data-hfse-rescue-settled={settled ? 'true' : undefined}>
      <h3 className="hfse-flex-rescue-title">{RESCUE_I18N.noMatchesFor} {rescueRangeLabel(checkin, checkout)}.</h3>
      <p className="hfse-flex-rescue-subtitle">{subtitle}</p>
      {folded.chips.length > 0 && (
        <div className="hfse-flex-rescue-chips">
          {folded.chips.map((chip) => (
            <RescueChipButton key={windowKey(chip)} chip={chip} onPick={pivot} />
          ))}
        </div>
      )}
    </div>
  )
}
